import React from 'react';
import {
    DATASET_TANKERS,
    datasetIdsWithoutIndividualVisibilityOption,
} from '../../models/Datasets';
import VisibilityIcon from '../icons/VisibilityIcon';
import IndividualIcon from '../icons/IndividualIcon';
import GroupOnlyPermanentIcon from '../icons/GroupOnlyPermanentIcon';
import GroupOnlyTemporaryIcon from '../icons/GroupOnlyTemporaryIcon';
import GroupOnlyTemporaryLabelRenderer from '../grid/renderers/GroupOnlyTemporaryLabelRenderer';
import IndividualLabelRenderer from '../grid/renderers/IndividualLabelRenderer';
import GroupOnlyPermanentLabelRenderer from '../grid/renderers/GroupOnlyPermanentLabelRenderer';
import {
    GROUP_ONLY_PERMANENT_TYPE_VALUE,
    GROUP_ONLY_TEMPORARY_TYPE_VALUE,
} from '../../constants/groupOnlyType';

export const PUBLIC_DOCUMENT_VALUE = 1;
export const PRIVATE_DOCUMENT_VALUE = 2;
export const GROUP_ONLY_DOCUMENT_VALUE = 3;
export const INDIVIDUAL_DOCUMENT_VALUE = 4;
export const GROUP_ONLY_PERMANENT_DOCUMENT_VALUE = 5;

interface CurrentGroup {
    name: string;
    datasetId?: number;
    groupOnlyExpiryPeriod?: number;
    groupSettings?: Record<string, unknown>;
}

interface DocumentSelection {
    groupOnly: boolean;
    groupOnlyType: string;
    isIndividual: boolean;
    privacy: boolean;
}

export const resolveDocumentSelection = (
    value: unknown,
    datasetId?: number
): DocumentSelection => {
    const groupOnly =
        value === GROUP_ONLY_DOCUMENT_VALUE ||
        value === GROUP_ONLY_PERMANENT_DOCUMENT_VALUE;
    const groupOnlyType =
        value === GROUP_ONLY_PERMANENT_DOCUMENT_VALUE
            ? GROUP_ONLY_PERMANENT_TYPE_VALUE
            : GROUP_ONLY_TEMPORARY_TYPE_VALUE;
    const isIndividual = value === INDIVIDUAL_DOCUMENT_VALUE;
    const privacy =
        value === PRIVATE_DOCUMENT_VALUE ||
        (groupOnly && datasetId !== DATASET_TANKERS) ||
        isIndividual;
    return { groupOnly, groupOnlyType, isIndividual, privacy };
};

export type DocumentName = 'Fixture' | 'Order';

type DocumentOption = {
    key: number;
    text: React.ReactNode;
    value: number;
    icon?: React.ReactNode;
};

// Builds the list of group-only options based on the dataset and group configuration:
// - Tankers: always includes the permanent option; also includes the temporary option
//   if the group has a non-zero groupOnlyExpiryPeriod.
// - All other datasets: returns only the temporary option when a groupOnlyExpiryPeriod
//   is configured, otherwise falls back to the permanent option.
const getGroupOnlyOptions = (
    datasetId: number | undefined,
    documentName: DocumentName,
    currentGroup: CurrentGroup
): DocumentOption[] => {
    const groupOnlyExpiryPeriod = currentGroup.groupOnlyExpiryPeriod ?? 0;
    const permanentOption: DocumentOption = {
        key: GROUP_ONLY_PERMANENT_DOCUMENT_VALUE,
        text: (
            <GroupOnlyPermanentLabelRenderer
                documentName={documentName}
                currentGroup={currentGroup}
            />
        ),
        value: GROUP_ONLY_PERMANENT_DOCUMENT_VALUE,
        icon: <GroupOnlyPermanentIcon />,
    };

    const temporaryOption: DocumentOption = {
        key: GROUP_ONLY_DOCUMENT_VALUE,
        text: (
            <GroupOnlyTemporaryLabelRenderer
                documentName={documentName}
                currentGroup={{ ...currentGroup, groupOnlyExpiryPeriod }}
            />
        ),
        value: GROUP_ONLY_DOCUMENT_VALUE,
        icon: <GroupOnlyTemporaryIcon />,
    };

    if (datasetId === DATASET_TANKERS) {
        return [
            permanentOption,
            ...(groupOnlyExpiryPeriod > 0 ? [temporaryOption] : []),
        ];
    }

    return [groupOnlyExpiryPeriod > 0 ? temporaryOption : permanentOption];
};

export const getDocumentOptions = (
    documentName: DocumentName,
    currentGroup: CurrentGroup
) => {
    const { datasetId } = currentGroup;

    const baseOptions: DocumentOption[] = [
        {
            key: PUBLIC_DOCUMENT_VALUE,
            text: `Public ${documentName}`,
            value: PUBLIC_DOCUMENT_VALUE,
        },
        {
            key: PRIVATE_DOCUMENT_VALUE,
            text: `P&C ${documentName}`,
            value: PRIVATE_DOCUMENT_VALUE,
        },
    ];

    const groupOnlyOptions = getGroupOnlyOptions(
        datasetId,
        documentName,
        currentGroup
    );

    const supportsIndividual =
        !datasetIdsWithoutIndividualVisibilityOption.includes(datasetId!);

    if (!supportsIndividual) {
        return [...baseOptions, ...groupOnlyOptions];
    }

    return [
        ...baseOptions,
        ...groupOnlyOptions,
        {
            key: INDIVIDUAL_DOCUMENT_VALUE,
            text: <IndividualLabelRenderer documentName={documentName} />,
            value: INDIVIDUAL_DOCUMENT_VALUE,
            icon: <IndividualIcon />,
        },
    ];
};
